import { ContentVideo } from './ContentVideo';
import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { apiRequest, ApiError } from '../../api/http';
import { PublicFooter, PublicHeader, usePublicLocale } from '../../layouts/PublicFrame';
import { Spinner } from '../../shared/components';
import './content.css';

type Review = Record<string, unknown>;
const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';
function mediaUrl(value: unknown): string {
  const raw = text(value);
  try { const url = new URL(raw); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
}
function photos(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(item => mediaUrl(typeof item === 'string' ? item : item?.url || item?.image_url)).filter(Boolean))];
}
function videos(value: unknown): { url: string; poster: string }[] {
  if (!Array.isArray(value)) return [];
  return value.filter(item => item && (item.status || 'ready') === 'ready').map(item => ({ url: mediaUrl(item.playback_url || item.url), poster: mediaUrl(item.thumbnail_url || item.poster_url) })).filter(item => item.url);
}

export function ContentPage({ id }: { id: string }) {
  const { locale, changeLocale } = usePublicLocale();
  const [review, setReview] = useState<Review | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [shareMessage, setShareMessage] = useState('');
  const [showLink, setShowLink] = useState(false);
  const pendingVideo = Array.isArray(review?.review_videos) && review.review_videos.some(item => ['processing', 'pending_upload'].includes(item?.status));
  const failedVideo = Array.isArray(review?.review_videos) && review.review_videos.some(item => ['failed', 'rejected'].includes(item?.status));
  useEffect(() => {
    const controller = new AbortController();
    setReview(null); setError(''); setShareMessage(''); setShowLink(false);
    apiRequest<Review>(`/reviews/${encodeURIComponent(id)}`, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setReview(data); })
      .catch(reason => { if (!controller.signal.aborted) setError(reason instanceof ApiError && [404, 410].includes(reason.status) ? 'ไม่พบโพสต์นี้ หรือโพสต์ถูกลบแล้ว' : 'โหลดโพสต์ไม่สำเร็จ กรุณาลองอีกครั้ง'); });
    return () => controller.abort();
  }, [id, attempt]);
  useEffect(() => {
    if (!pendingVideo) return;
    const controller = new AbortController();
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const data = await apiRequest<Review>(`/reviews/${encodeURIComponent(id)}`, { signal: controller.signal });
        if (!stopped) setReview(data);
      } catch { /* Keep the current post visible through temporary network errors. */ }
      finally { if (!stopped) timer = setTimeout(refresh, 8000); }
    };
    timer = setTimeout(refresh, 8000);
    return () => { stopped = true; clearTimeout(timer); controller.abort(); };
  }, [id, pendingVideo]);
  const title = text(review?.display_title || review?.title);
  const body = text(review?.display_body ?? review?.review_text ?? review?.comment ?? review?.body);
  const author = text(review?.aka_name || review?.expert_name || review?.username) || 'สมาชิก Beaw';
  const place = text(review?.cafe_name || review?.place_name);
  const avatar = mediaUrl(review?.avatar_url);
  const date = new Date(text(review?.created_at));
  useEffect(() => {
    const previous = document.title;
    document.title = `${title || place || 'โพสต์จากชุมชน'} | Beaw Club`;
    return () => { document.title = previous; };
  }, [title, place]);
  const shareUrl = new URL(`/content/${encodeURIComponent(id)}`, window.location.origin).href;
  const shareText = [place, title, body.slice(0, 300)].filter(Boolean).join('\n');
  async function share() {
    try {
      if (navigator.share) { await navigator.share({ title: place || title || 'Beaw Club', text: shareText, url: shareUrl }); return; }
    } catch (reason) { if (reason instanceof DOMException && reason.name === 'AbortError') return; }
    try { await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`); setShareMessage('คัดลอกลิงก์โพสต์แล้ว'); }
    catch { setShowLink(true); setShareMessage('คัดลอกลิงก์ด้านล่างเพื่อแชร์โพสต์'); }
  }
  return <div className="public-content"><PublicHeader locale={locale} onLocaleChange={changeLocale} />
    <main className="content-view">
      {error ? <section className="content-view__status" role="alert"><h1>{error}</h1><button className="button button--primary" onClick={() => setAttempt(value => value + 1)}>ลองอีกครั้ง</button></section>
        : !review ? <div className="content-view__status"><Spinner label="กำลังโหลดโพสต์" /></div>
        : <article className="content-view__post">
          <header className="content-view__author">{avatar ? <img src={avatar} alt="" /> : <span className="content-view__avatar">{author.slice(0, 1)}</span>}<div><strong>{author}</strong>{!Number.isNaN(date.getTime()) && <time dateTime={date.toISOString()}>{date.toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</time>}</div><button type="button" className="content-view__share" onClick={() => void share()} aria-label="แชร์โพสต์"><svg width="20" height="20" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="miter" aria-hidden="true"><path d="M3 5 29 16 3 13Z M3 19 29 16 3 27Z" /></svg> แชร์</button></header>
          {place && <div className="content-view__place"><MapPin size={18} /><span>{place}</span></div>}
          {title && <h1>{title}</h1>}{body && <p className="content-view__body">{body}</p>}
          {pendingVideo && <p role="status">กำลังประมวลผลคลิป… คลิปจะแสดงอัตโนมัติเมื่อพร้อม</p>}
          {failedVideo && <p role="status">ประมวลผลคลิปไม่สำเร็จ</p>}
          <div className="content-view__media">{photos(review.photos).map((url, index) => <img key={url} src={url} alt={`รูปประกอบโพสต์ ${index + 1}`} loading={index ? 'lazy' : 'eager'} />)}{videos(review.review_videos).map(video => <ContentVideo key={video.url} src={video.url} poster={video.poster || undefined} />)}</div>
          {shareMessage && <p role="status">{shareMessage}</p>}{showLink && <input aria-label="ลิงก์โพสต์" readOnly value={shareUrl} onFocus={event => event.target.select()} />}
        </article>}
    </main><PublicFooter /></div>;
}
